import React, { useEffect, useState } from "react";
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SquareFootOutlinedIcon from "@mui/icons-material/SquareFootOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import { useNavigate, useParams } from "react-router-dom";
import PropertyForm from "./PropertyForm";
import PropertyWorkspace from "./PropertyWorkspace";
import PropertyImageGallery from "./PropertyImageGallery";
import PropertyRecords from "./PropertyRecords";
import PropertyTenantsLeases from "./PropertyTenantsLeases";
import { getProperty, listPropertyPhase4Records, updateProperty, updatePropertyWorkspace } from "./propertyApi";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import { getPropertyStatusSx } from "./propertyStatusStyles";

const panelSx = {
  border: "1px solid #dfe5ec",
  borderRadius: 2,
  boxShadow: "0 4px 14px rgba(15,23,42,.055)",
};
const metricLabelSx = { display: "block", color: "text.secondary", fontSize: 13, lineHeight: 1.25 };
const metricValueSx = { color: BRAND_BLUE, fontSize: 16, fontWeight: 700, lineHeight: 1.3 };
const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
};

export default function PropertyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tenancySummary, setTenancySummary] = useState({ tenants: [], leases: [] });
  const [workspaceTab, setWorkspaceTab] = useState("documents");

  useEffect(() => {
    let active = true;
    Promise.all([
      getProperty(id),
      Promise.allSettled([
        listPropertyPhase4Records(id, "tenants"),
        listPropertyPhase4Records(id, "leases"),
      ]),
    ])
      .then(([record, [tenantsResult, leasesResult]]) => {
        if (active) {
          setProperty(record);
          setTenancySummary({
            tenants: tenantsResult.status === "fulfilled" ? tenantsResult.value : [],
            leases: leasesResult.status === "fulfilled" ? leasesResult.value : [],
          });
        }
      })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.status === 404 ? "Property not found." : requestError?.response?.data?.detail || "Property could not be loaded.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const activeLeases = tenancySummary.leases.filter((lease) => String(lease.status).toLowerCase() === "active");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextLeaseExpiration = activeLeases
    .filter((lease) => {
      const endDate = new Date(`${lease.endDate}T00:00:00`);
      return !Number.isNaN(endDate.getTime()) && endDate >= today;
    })
    .sort((left, right) => String(left.endDate).localeCompare(String(right.endDate)))[0];
  const tenantNames = tenancySummary.tenants
    .map((tenant) => tenant.tenantName)
    .filter(Boolean)
    .join(", ");

  const handleUpdate = async (form) => {
    setSaving(true);
    setError("");
    try {
      const updated = await updateProperty(id, form);
      setProperty(updated);
      setEditing(false);
      setSuccess("Property updated successfully.");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Property could not be updated.");
    } finally {
      setSaving(false);
    }
  };

  const handleWorkspaceUpdate = async (workspace) => {
    setSaving(true);
    setError("");
    try {
      const updated = await updatePropertyWorkspace(id, workspace);
      setProperty(updated);
      setSuccess("Property workspace updated successfully.");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Property workspace could not be updated.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ py: 12, display: "flex", justifyContent: "center" }}><CircularProgress sx={{ color: BRAND_GREEN }} /></Box>;
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0, minHeight: "100vh", boxSizing: "border-box", p: { xs: 2, md: 3 }, bgcolor: "#f7f9fc" }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/properties")} sx={{ mb: 2, px: 0, color: BRAND_BLUE, textTransform: "none", fontWeight: 700 }}>
        Back to properties
      </Button>

      {error && <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>{error}</Alert>}

      {!property ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: "center", border: `1px solid ${alpha(BRAND_BLUE, 0.12)}`, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700 }}>This property is unavailable</Typography>
          <Button onClick={() => navigate("/properties")} sx={{ mt: 2, textTransform: "none" }}>Return to properties</Button>
        </Paper>
      ) : editing ? (
        <PropertyForm
          key={property.updatedAt}
          initialValue={property}
          title="Edit property"
          description="Update the property details below."
          submitLabel="Save changes"
          saving={saving}
          onSubmit={handleUpdate}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          <Paper elevation={0} sx={{ ...panelSx, overflow: "hidden" }}>
            <PropertyImageGallery images={property.images} propertyName={property.name} />
            <Box sx={{ p: { xs: 1.5, md: 2 } }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" }, alignItems: "center", gap: { xs: 1.5, sm: 2 } }}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Typography variant="h4" component="h1" sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: { xs: 26, md: 30 }, lineHeight: 1.15 }}>{property.name}</Typography>
                    <Chip
                      label={property.status || "Occupied"}
                      size="small"
                      variant="outlined"
                      sx={{ height: 24, fontWeight: 800, ...getPropertyStatusSx(property.status) }}
                    />
                  </Box>
                  <Box sx={{ mt: 1, display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                    <LocationOnOutlinedIcon sx={{ mt: "2px", color: "error.main", fontSize: 18, flex: "0 0 auto" }} />
                    <Typography variant="body2" sx={{ color: BRAND_BLUE, lineHeight: 1.45 }}>
                      {[property.streetAddress, `${[property.city, property.state].filter(Boolean).join(", ")} ${property.zipCode || ""}`.trim(), property.country].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                  <Button size="small" variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setEditing(true)} sx={{ bgcolor: BRAND_BLUE, textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { bgcolor: BRAND_BLUE, boxShadow: "none" } }}>
                    Edit property
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ ...panelSx, mt: 2, px: { xs: 2, md: 2.5 }, py: 2 }}>
            <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 750, fontSize: 17 }}>Property overview</Typography>
            <Box sx={{ mt: 1.25, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" } }}>
              {[
                { label: "Square footage", value: `${Number(property.squareFeet || 0).toLocaleString()} sq. ft.`, icon: <SquareFootOutlinedIcon /> },
                { label: "Date added", value: formatDate(property.createdAt), icon: <CalendarTodayOutlinedIcon /> },
                { label: "Last updated", value: formatDate(property.updatedAt), icon: <UpdateOutlinedIcon /> },
              ].map((item) => (
                <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75, px: { xs: 0, sm: 1.5 }, borderTop: { xs: `1px solid ${alpha(BRAND_BLUE, 0.09)}`, sm: 0 }, borderLeft: { xs: 0, sm: `1px solid ${alpha(BRAND_BLUE, 0.09)}` }, "&:first-of-type": { borderTop: 0, borderLeft: 0, pt: { xs: 0, sm: 0.75 }, pl: 0 } }}>
                  <Box sx={{ flex: "0 0 auto", width: 30, height: 30, borderRadius: 1.25, display: "grid", placeItems: "center", color: BRAND_GREEN, bgcolor: alpha(BRAND_GREEN, 0.1), "& svg": { fontSize: 18 } }}>{item.icon}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={metricLabelSx}>{item.label}</Typography>
                    <Typography sx={metricValueSx}>{item.value}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
          <Paper elevation={0} sx={{ ...panelSx, mt: 2, px: { xs: 2, md: 2.5 }, py: 2 }}>
            <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 750, fontSize: 17 }}>Building information</Typography>
            <Box component="dl" sx={{ m: 0, mt: 1.25, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", columnGap: 2, rowGap: 0 }}>
              {[
                ["Property type", property.propertyType || "Not specified"],
                ["Current status", property.status || "Occupied"],
                ["Year built", property.yearBuilt || "Not specified"],
                ["Number of floors", property.numberOfFloors === "" || property.numberOfFloors == null ? "Not specified" : Number(property.numberOfFloors).toLocaleString()],
                ["Lot size", property.lotSizeAcres === "" || property.lotSizeAcres == null ? "Not specified" : `${Number(property.lotSizeAcres).toLocaleString()} acres`],
                ["Tenants", tenantNames || "None recorded"],
                ["Next lease expiration", nextLeaseExpiration ? formatDate(`${nextLeaseExpiration.endDate}T00:00:00`) : "None scheduled"],
              ].map(([label, value]) => (
                <Box key={label} sx={{ py: 1, borderBottom: `1px solid ${alpha(BRAND_BLUE, 0.09)}` }}>
                  <Typography component="dt" sx={{ ...metricLabelSx, color: BRAND_GREEN }}>{label}</Typography>
                  <Typography component="dd" sx={{ ...metricValueSx, m: 0, mt: 0.25, color: BRAND_BLUE }}>{value}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 0.75, pt: 1.25, borderTop: `1px solid ${alpha(BRAND_BLUE, 0.09)}` }}>
              <Typography sx={{ ...metricLabelSx, color: BRAND_GREEN }}>Building description</Typography>
              <Typography sx={{ mt: 0.25, color: BRAND_BLUE, fontSize: 15, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                {property.buildingDescription || "No building description has been added."}
              </Typography>
            </Box>
            <Box sx={{ mt: 0.75, pt: 1.25, borderTop: `1px solid ${alpha(BRAND_BLUE, 0.09)}` }}>
              <Typography sx={{ ...metricLabelSx, color: BRAND_GREEN }}>Available utilities</Typography>
              <Typography sx={{ mt: 0.25, color: BRAND_BLUE, fontSize: 15, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                {property.utilitiesAvailable || "No utility information has been added."}
              </Typography>
            </Box>
          </Paper>
          <Paper elevation={0} sx={{ ...panelSx, mt: 2, overflow: "hidden", boxShadow: "none" }}>
            <Tabs value={workspaceTab} onChange={(_, value) => setWorkspaceTab(value)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label="Property workspace sections" sx={{ minHeight: 44, "& .MuiTabs-flexContainer": { flexWrap: "nowrap" }, "& .MuiTab-root": { minWidth: "auto", minHeight: 44, px: 1.5, flexShrink: 0, textTransform: "none", fontWeight: 700 }, "& .Mui-selected": { color: `${BRAND_BLUE} !important` }, "& .MuiTabs-indicator": { bgcolor: BRAND_GREEN, height: 3 } }}>
              {[
                ["documents", "Documents"], ["tenants", "Tenants"],
                ["leases", "Leases"], ["map", "Map"], ["images", "Images"],
              ].map(([value, label]) => <Tab key={value} value={value} label={label} />)}
            </Tabs>
          </Paper>
          <PropertyRecords propertyId={property.id} activeTab={workspaceTab} visible={workspaceTab === "documents"} />
          {(["tenants", "leases"].includes(workspaceTab)) && <PropertyTenantsLeases propertyId={property.id} resource={workspaceTab} onChanged={setSuccess} onRecordsChange={(resource, records) => setTenancySummary((current) => ({ ...current, [resource]: records }))} />}
          <PropertyWorkspace property={property} saving={saving} onSave={handleWorkspaceUpdate} activeTab={workspaceTab} visible={workspaceTab === "map"} />
          {workspaceTab === "images" && (
            <Paper elevation={0} sx={{ ...panelSx, mt: 2, overflow: "hidden" }}>
              <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2, pb: 1 }}>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 750, fontSize: 17 }}>
                  Property images
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {property.images?.length
                    ? `${property.images.length.toLocaleString()} ${property.images.length === 1 ? "image" : "images"} · The first image is the cover.`
                    : "No images have been added to this property."}
                </Typography>
              </Box>
              <PropertyImageGallery images={property.images} propertyName={property.name} />
            </Paper>
          )}
        </>
      )}

      <Snackbar open={Boolean(success)} autoHideDuration={4000} onClose={() => setSuccess("")}>
        <Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert>
      </Snackbar>

    </Box>
  );
}
